import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { Threebox } from 'threebox-plugin';
import { SearchBox } from "@mapbox/search-js-react";
import { Button, Modal, Upload, Row, Col, Switch, Progress, Spin, Input } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import className from 'classnames'
import Dropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next'; // Added for translation support
import { uploadMedia } from '../../../../internal/List/images'

import 'mapbox-gl/dist/mapbox-gl.css';
import 'antd/dist/antd.css';
import './Map.css';

const { TextArea } = Input;

const Mapbox = ({ mediaDat, onDropppedZip, onGeoLocation, onDeleteMarker, isMarkerBased, zipLoading, zipSizeCheck, isZip, onSwitchInteractionMode, switchInteractive }) => {
  const { t } = useTranslation(); // Initialize translation hook
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [coordinates, setCoordinates] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);

  // Modal states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentMarkerIndex, setCurrentMarkerIndex] = useState(0);
  const [markerDescription, setMarkerDescription] = useState('');

  // Store marker data with format: [{text, longitude, latitude}, ...]
  const [markersData, setMarkersData] = useState([]);

  // Search functionality states from App.jsx
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Styles for new components
  const sectionStyle = { marginBottom: '20px' };
  const headingStyle = { fontSize: '16px', fontWeight: '600', marginBottom: '5px', textAlign: 'left' };
  const descriptionStyle = { fontSize: '14px', color: '#666', marginBottom: '10px' };
  const modalInnerStyle = { padding: '15px', border: '1px dashed #d9d9d9', borderRadius: '4px' };
  const dropzoneTextStyle = { textAlign: 'center', marginTop: '10px' };
  const textAreaStyle = { marginTop: '15px', marginBottom: '15px' };

  // Refs to track the latest state values
  const selectedFeatureRef = useRef(null);
  const markersRef = useRef([]);
  const popupBtnListenersRef = useRef({});

  // Function to check if upload is enabled
  const isUploadEnabled = () => {
    return markerDescription.trim() !== '' && (mediaDat.hasOwnProperty('ar_files'));
  };

  // Function to handle modal visibility
  const handleModalOpen = (index) => {
    setCurrentMarkerIndex(index);

    // Set description from stored marker data if available
    const existingData = markersData.find(m => m.index === index);
    setMarkerDescription(existingData && existingData.text || '');

    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    clearModalFields();
  };

  // Clear fields after modal is closed
  const clearModalFields = () => {
    setMarkerDescription('');
  };

  // Handle description change
  const handleDescriptionChange = (e) => {
    setMarkerDescription(e.target.value);
  };

  // Handle finishing the upload - Store marker data in array
  const handleUpload = () => {
    // Get marker coordinates
    const markerCoords = markersRef.current && markersRef.current[currentMarkerIndex] &&
      markersRef.current[currentMarkerIndex].marker.getLngLat();

    if (markerCoords) {
      // Create marker data object with format {text, longitude, latitude}
      const markerData = {
        index: currentMarkerIndex,
        text: markerDescription,
        longitude: markerCoords.lng,
        latitude: markerCoords.lat
      };

      // Create updated markers data
      const updatedMarkersData = [...markersData, markerData].sort((a, b) => b.index - a.index);

      // Update markersData state
      setMarkersData(updatedMarkersData);

      // Pass the updated markers data to parent component through props
      if (onGeoLocation) {
        onGeoLocation(updatedMarkersData);
      }
    }
    // Close the modal and clear fields
    setIsModalVisible(false);
    clearModalFields();
  };


  // Function to add marker at a specific location (from search results)
  const addMarkerAtLocation = async (lngLat) => {
    const newIndex = markersRef.current.length;

    // Define marker colors and images
    const markerColors = ['red', 'blue', 'green', 'yellow'];
    const markerImages = [
      'https://docs.mapbox.com/mapbox-gl-js/assets/washington-monument.jpg',
      'https://docs.mapbox.com/help/demos/custom-markers-gl-js/mapbox-icon.png',
      'https://docs.mapbox.com/mapbox-gl-js/assets/cat.png',
      'https://docs.mapbox.com/mapbox-gl-js/assets/dog-park.png'
    ];

    // Create marker element
    const el = document.createElement('div');
    el.className = 'custom-marker';
    el.style.backgroundImage = `url('${markerImages[newIndex % markerImages.length] || markerImages[0]}')`;
    el.style.backgroundSize = 'cover';
    el.style.width = '40px';
    el.style.height = '40px';
    el.style.borderRadius = '50%';
    el.style.cursor = 'pointer';
    el.style.border = `3px solid ${markerColors[newIndex % markerColors.length]}`;

    // Create popup
    const popup = new mapboxgl.Popup({
      offset: 25,
      closeButton: true,
      closeOnClick: false,
      className: 'marker-popup',
      maxWidth: '300px'
    }).setHTML(createPopupContent(lngLat[0], lngLat[1], newIndex));

    // Create marker
    const marker = new mapboxgl.Marker({
      element: el,
      draggable: true
    })
      .setLngLat(lngLat)
      .setPopup(popup)
      .addTo(mapRef.current);

    // Store marker reference
    markersRef.current.push({
      marker,
      popup,
      id: newIndex,
      color: markerColors[newIndex % markerColors.length]
    });

    // Setup marker events
    setupMarkerEvents(marker, popup, newIndex);

    // Update coordinates state
    setCoordinates(prev => {
      const newCoords = [...prev];
      newCoords[newIndex] = `Marker ${newIndex + 1}: Longitude: ${lngLat[0].toFixed(4)}, Latitude: ${lngLat[1].toFixed(4)}`;
      return newCoords;
    });
  };

  // Setup event listeners for popup buttons - IMPROVED VERSION
  const setupPopupButtonListeners = (index) => {
    setTimeout(() => {
      const uploadBtn = document.getElementById(`upload-btn-${index}`);
      if (uploadBtn) {
        // Remove any existing listeners to avoid duplicates
        const oldListener = popupBtnListenersRef.current[`btn-${index}`];
        if (oldListener) {
          uploadBtn.removeEventListener('click', oldListener);
        }

        // Add new listener with proper event handling
        const listener = (e) => {
          e.preventDefault();
          e.stopPropagation();
          handleModalOpen(index);
          return false; // Prevent further propagation
        };

        uploadBtn.addEventListener('click', listener);

        // Save reference to the listener for cleanup
        popupBtnListenersRef.current[`btn-${index}`] = listener;
      }
    }, 100); // Small delay to ensure DOM is ready
  };

  // Function to close all marker popups
  const closeAllPopups = () => {
    markersRef.current.forEach(markerObj => {
      if (markerObj.marker && markerObj.popup.isOpen()) {
        markerObj.marker.getPopup().remove();
      }
    });
  };

  // Function to create popup content with buttons
  const createPopupContent = (lng, lat, index) => `
    <div style="color: #333; font-weight: bold; position: relative; z-index: 10;">
      <h3 style={{ margin: '0 0 5px 0', color: (markersRef.current[index] && markersRef.current[index].color) || 'red' }}>
           Marker ${index}
      </h3
       <p style="margin: 0; font-size: 12px; color: #000;">
        Longitude: ${lng.toFixed(4)}<br>
        Latitude: ${lat.toFixed(4)}
      </p>
      <p style="margin: 5px 0 0; font-size: 12px; color: #666;">
        Drag me to update coordinates!
      </p>
      <div style="position: relative; z-index: 999; margin-top: 8px; display: flex; gap: 8px;">
        <button 
          id="upload-btn-${index}" 
          style="padding: 4px 8px; background-color: #1890ff; color: white; border: none; border-radius: 2px; cursor: pointer; position: relative; z-index: 1000; pointer-events: auto; display: flex; align-items: center; gap: 4px;"
          onclick="event.stopPropagation(); return false;"
        >
          Upload Assets
        </button>
        <button 
          id="delete-btn-${index}" 
          style="padding: 4px 8px; background-color: #ff4d4f; color: white; border: none; border-radius: 2px; cursor: pointer; position: relative; z-index: 1100; pointer-events: auto; display: flex; align-items: center; gap: 4px;"
          onclick="event.stopPropagation(); return false;"
        >
           Delete
        </button>
      </div>
    </div>
  `;

  // Function to setup marker events
  const setupMarkerEvents = (marker, popup, index) => {
    marker.on('dragend', () => {
      const lngLat = marker.getLngLat();
      setCoordinates(prev => {
        const newCoords = [...prev];
        newCoords[index] = `Marker ${index + 1}: Longitude: ${lngLat.lng.toFixed(4)}, Latitude: ${lngLat.lat.toFixed(4)}`;
        return newCoords;
      });
      popup.setHTML(createPopupContent(lngLat.lng, lngLat.lat, index));
      if (popup.isOpen()) {
        setupPopupButtonListeners(index);
      }
    });

    marker.getElement().addEventListener('click', (e) => {
      e.stopPropagation();
      if (selectedFeatureRef.current) {
        mapRef.current.setFeatureState(selectedFeatureRef.current, {
          selected: false
        });
        setSelectedFeature(null);
      }
      closeAllPopups();
      marker.togglePopup();
      if (popup.isOpen()) {
        setupPopupButtonListeners(index);
      }
    });

    // Add delete button event listener
    popup.on('open', () => {
      const deleteBtn = document.getElementById(`delete-btn-${index}`);
      if (deleteBtn) {
        // Remove any existing listeners to avoid duplicates
        const newDeleteBtn = deleteBtn.cloneNode(true);
        deleteBtn.parentNode.replaceChild(newDeleteBtn, deleteBtn);

        newDeleteBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Remove marker from map
          marker.remove();

          // Remove from markers array
          markersRef.current = markersRef.current.filter(m => m.id !== index);

          // Update coordinates state
          setCoordinates(prev => prev.filter((_, i) => i !== index));

          // Update markers data
          setMarkersData(prev => prev.filter(m => m.index !== index));

          // Call onDeleteMarker with the deleted index
          if (onDeleteMarker) {
            onDeleteMarker(index);
          }

          // Close popup
          popup.remove();
        });
      }
    });
  };

  // Search functionality from App.jsx
  const handleSearchChange = (newValue) => {
    setSearchValue(newValue);
    setSearchResults(null);
  };

  const handleSearchSubmit = async () => {
    // Clear previous results
    setError(null);
    setSearchResults(null);

    // Check if input matches coordinate pattern
    const coordPattern = /^(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)$/;
    if (coordPattern.test(searchValue)) {
      setIsLoading(true);

      try {
        const parts = searchValue.split(',').map(part => parseFloat(part.trim()));

        // Determine if format is lat,lng or lng,lat by checking ranges
        let lng, lat;

        if (Math.abs(parts[0]) <= 90 && Math.abs(parts[1]) <= 180) {
          // Format is likely lat,lng
          lat = parts[0];
          lng = parts[1];
        } else {
          // Format is likely lng,lat
          lng = parts[0];
          lat = parts[1];
        }

        // Validate coordinates
        if (Math.abs(lng) <= 180 && Math.abs(lat) <= 90) {
          // Fly to the coordinates
          mapRef.current.flyTo({
            center: [lng, lat],
            zoom: 15
          });

          // Add a marker at the location
          addMarkerAtLocation([lng, lat]);

          // Call Mapbox Reverse Geocoding API
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=pk.eyJ1IjoibWFwYm94LXJhYW5hbiIsImEiOiJjbTh4eGZpbmwwOHdzMnNwbHlvaTFyNWdxIn0.uy0dfRpvkNH-r5R1KvtnWQ&types=address,poi`
          );

          if (!response.ok) {
            throw new Error('Failed to fetch location data');
          }

          const data = await response.json();
          setSearchResults(data.features);
        } else {
          setError('Invalid coordinates. Longitude must be between -180 and 180, latitude between -90 and 90.');
        }
      } catch (err) {
        setError(err.message || 'An error occurred while processing coordinates');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle search result selection
  const handleSearchSelection = (result) => {
    // If a result with coordinates is selected, fly to that location
    if (result && result.features && result.features.length > 0) {
      const [feature] = result.features;
      if (feature.geometry && feature.geometry.coordinates) {
        const coordinates = feature.geometry.coordinates;

        // Fly to the location
        mapRef.current.flyTo({
          center: coordinates,
          zoom: 15
        });

        // Add a marker at the location
        addMarkerAtLocation(coordinates);
      }
    }
  };

  useEffect(() => {
    mapboxgl.accessToken = 'pk.eyJ1IjoibWFwYm94LXJhYW5hbiIsImEiOiJjbTh4eGZpbmwwOHdzMnNwbHlvaTFyNWdxIn0.uy0dfRpvkNH-r5R1KvtnWQ';

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-74.0066, 40.7135],
      zoom: 15.5,
      pitch: 45,
      bearing: -17.6,
      antialias: true
    });

    // Add geolocate control for current location tracking
    mapRef.current.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true,
        showUserHeading: true
      })
    );

    // Custom Add Marker Control
    class AddMarkerControl {
      onAdd(map) {
        this._map = map;
        this._container = document.createElement('div');
        this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
        this._container.style.cssText = `
          position: absolute;
          margin-right: 4em;
          right: 10px;
          z-index: 1000;
        `;

        const button = document.createElement('button');
        button.className = 'mapboxgl-ctrl-icon';
        button.type = 'button';
        button.title = 'Add Marker';
        button.innerHTML = '<span class="mapboxgl-ctrl-icon" style="margin-left: 6px;margin-top: 6px;" aria-hidden="true">📍</span>';
        button.style.cssText = `
          width: 30px;
          height: 30px;
          background-color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        `;

        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const center = map.getCenter();
          const newIndex = markersRef.current.length;
          addMarkerAtLocation([center.lng, center.lat], newIndex);
        });

        this._container.appendChild(button);
        return this._container;
      }

      onRemove() {
        this._container.parentNode.removeChild(this._container);
        this._map = undefined;
      }
    }

    // Add the custom control to the map
    mapRef.current.addControl(new AddMarkerControl(), 'top-right');

    mapRef.current.on('load', () => {
      // Add 3D buildings
      const layers = mapRef.current.getStyle().layers;

      var labelLayerId = null;
      var labelLayer = layers.find(function (layer) {
        return layer.type === 'symbol' && layer.layout && layer.layout['text-field'];
      });

      if (labelLayer) {
        labelLayerId = labelLayer.id;
        mapRef.current.addLayer(
          {
            id: 'add-3d-buildings',
            source: 'composite',
            'source-layer': 'building',
            filter: ['==', 'extrude', 'true'],
            type: 'fill-extrusion',
            minzoom: 15,
            paint: {
              'fill-extrusion-color': '#aaa',
              'fill-extrusion-height': [
                'interpolate',
                ['linear'],
                ['zoom'],
                15,
                0,
                15.05,
                ['get', 'height']
              ],
              'fill-extrusion-base': [
                'interpolate',
                ['linear'],
                ['zoom'],
                15,
                0,
                15.05,
                ['get', 'min_height']
              ],
              'fill-extrusion-opacity': 0.6
            }
          },
          labelLayerId
        );
      }

      // Add airports source and layer from second example
      mapRef.current.addSource('airports', {
        type: 'vector',
        url: 'mapbox://mapbox.04w69w5j',
        promoteId: 'abbrev'
      });

      mapRef.current.addLayer({
        id: 'airport',
        source: 'airports',
        'source-layer': 'ne_10m_airports',
        type: 'circle',
        paint: {
          'circle-color': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            '#f00',
            '#4264fb'
          ],
          'circle-radius': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            6,
            ['boolean', ['feature-state', 'highlight'], false],
            6,
            4
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });

      // Add custom 3D model using Threebox
      mapRef.current.addLayer({
        id: 'custom-threebox-model',
        type: 'custom',
        renderingMode: '3d',
        onAdd: function () {
          window.tb = new Threebox(
            mapRef.current,
            mapRef.current.getCanvas().getContext('webgl'),
            { defaultLights: true }
          );
          const scale = 3.2;
          const options = {
            obj: 'https://docs.mapbox.com/mapbox-gl-js/assets/metlife-building.gltf',
            type: 'gltf',
            scale: { x: scale, y: scale, z: 2.7 },
            units: 'meters',
            rotation: { x: 90, y: -90, z: 0 }
          };

          window.tb.loadObj(options, (model) => {
            model.setCoords([-74.0066, 40.7135]);
            model.setRotation({ x: 0, y: 0, z: 241 });
            window.tb.add(model);
          });
        },
        render: function () {
          window.tb && window.tb.update();
        }
      });

      // Setup interactions for airport features
      mapRef.current.on('click', 'airport', (e) => {
        if (selectedFeatureRef.current) {
          mapRef.current.setFeatureState(selectedFeatureRef.current, {
            selected: false
          });
        }

        if (e.features.length > 0) {
          const feature = {
            id: e.features[0].id,
            source: 'airports',
            sourceLayer: 'ne_10m_airports'
          };

          mapRef.current.setFeatureState(feature, { selected: true });
          setSelectedFeature(e.features[0]);

          // Close all marker popups when selecting an airport
          closeAllPopups();
        }
      });

      // Clicking elsewhere on the map deselects the airport and marker
      mapRef.current.on('click', (e) => {
        // Check if the click was on an airport
        const features = mapRef.current.queryRenderedFeatures(e.point, { layers: ['airport'] });
        if (features.length === 0) {
          // Deselect airport feature if one is selected
          if (selectedFeatureRef.current) {
            mapRef.current.setFeatureState(selectedFeatureRef.current, {
              selected: false
            });
            setSelectedFeature(null);
          }

          // Check if the click was directly on the map (not on a marker)
          // If so, close all marker popups
          const markerElements = document.querySelectorAll('.custom-marker');
          let clickedOnMarker = false;

          for (let i = 0; i < markerElements.length; i++) {
            const rect = markerElements[i].getBoundingClientRect();
            if (
              e.originalEvent.clientX >= rect.left &&
              e.originalEvent.clientX <= rect.right &&
              e.originalEvent.clientY >= rect.top &&
              e.originalEvent.clientY <= rect.bottom
            ) {
              clickedOnMarker = true;
              break;
            }
          }

          if (!clickedOnMarker) {
            closeAllPopups();
          }
        }
      });

      // Hover effects for airports
      mapRef.current.on('mouseenter', 'airport', (e) => {
        mapRef.current.getCanvas().style.cursor = 'pointer';
        if (e.features.length > 0) {
          const feature = {
            id: e.features[0].id,
            source: 'airports',
            sourceLayer: 'ne_10m_airports'
          };
          mapRef.current.setFeatureState(feature, { highlight: true });
        }
      });

      mapRef.current.on('mouseleave', 'airport', (e) => {
        mapRef.current.getCanvas().style.cursor = '';
        if (e.features.length > 0) {
          const feature = {
            id: e.features[0].id,
            source: 'airports',
            sourceLayer: 'ne_10m_airports'
          };
          mapRef.current.setFeatureState(feature, { highlight: false });
        }
      });
    });

    // Add custom CSS to enhance popup buttons
    const style = document.createElement('style');
    style.textContent = `
      .mapboxgl-popup {
        z-index: 10;
      }
      .mapboxgl-popup-content {
        z-index: 11;
        pointer-events: auto !important;
        position: relative; /* Ensure proper positioning context */
      }
      .marker-popup .mapboxgl-popup-content {
        pointer-events: auto !important;
        padding-top: 30px; /* Add space at top for the close button */
      }
      /* Ensure buttons inside popups are clickable */
      .mapboxgl-popup-content button {
        pointer-events: auto !important;
        position: relative;
        z-index: 1000;
      }
      /* Reposition the close button to top right */
      .mapboxgl-popup-close-button {
        position: absolute !important;
        right: 5px !important;
        top: 5px !important;
        font-size: 16px !important;
        z-index: 2000 !important;
        border: none !important;
        background: transparent !important;
        cursor: pointer !important;
        padding: 0 5px !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      // Clean up event listeners
      Object.entries(popupBtnListenersRef.current).forEach(([key, listener]) => {
        const btnId = key.replace('btn-', 'upload-btn-');
        const btn = document.getElementById(btnId);
        if (btn) {
          btn.removeEventListener('click', listener);
        }
      });

      document.head.removeChild(style);
      mapRef.current.remove();
    };
  }, []);

  // Sync Ref with State for selected feature
  useEffect(() => {
    selectedFeatureRef.current = selectedFeature ? {
      id: selectedFeature.id,
      source: 'airports',
      sourceLayer: 'ne_10m_airports'
    } : null;
  }, [selectedFeature]);

  console.log("markersData log", markersData);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>

      <div style={{
        position: 'absolute',
        top: '10px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10,
        width: '80%',
        maxWidth: '600px',
        display: 'flex',
        gap: '10px'
      }}>
        <div style={{ flexGrow: 1 }}>
          <SearchBox
            options={{
              proximity: {
                lng: -74.0066,
                lat: 40.7135,
              },
            }}
            value={searchValue}
            onChange={handleSearchChange}
            onRetrieve={handleSearchSelection}
            accessToken="pk.eyJ1IjoibWFwYm94LXJhYW5hbiIsImEiOiJjbTh4eGZpbmwwOHdzMnNwbHlvaTFyNWdxIn0.uy0dfRpvkNH-r5R1KvtnWQ"
          />
        </div>
        <button
          onClick={handleSearchSubmit}
          style={{
            padding: '0 15px',
            backgroundColor: '#4264fb',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          Search
        </button>
      </div>

      <div ref={mapContainerRef} id="map" style={{ width: '100%', height: '100%' }}></div>

      {/* Loading indicator */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: '60px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'white',
          padding: '8px 15px',
          borderRadius: '4px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
          zIndex: 10
        }}>
          Loading...
        </div>
      )}

      {/* Error display */}
      {error && (
        <div style={{
          position: 'absolute',
          top: '60px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#fee2e2',
          border: '1px solid #fca5a5',
          color: '#b91c1c',
          padding: '8px 15px',
          borderRadius: '4px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
          zIndex: 10,
          maxWidth: '500px'
        }}>
          {error}
        </div>
      )}

      {/* Search results panel */}
      {searchResults && searchResults.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '60px',
          left: '10px',
          width: '300px',
          maxHeight: '80vh',
          overflowY: 'auto',
          background: 'white',
          borderRadius: '4px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 10
        }}>
          <h3 style={{
            padding: '10px 15px',
            margin: 0,
            borderBottom: '1px solid #e5e7eb',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>Results</h3>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0
          }}>
            {searchResults.map((result, index) => (
              <li key={index} style={{
                padding: '10px 15px',
                borderBottom: index === searchResults.length - 1 ? 'none' : '1px solid #e5e7eb',
                cursor: 'pointer'
              }} onClick={() => {
                mapRef.current.flyTo({
                  center: result.geometry.coordinates,
                  zoom: 15
                });
                // Add marker when clicking on a search result
                addMarkerAtLocation(result.geometry.coordinates);
              }}>
                <p style={{
                  margin: '0 0 5px 0',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>{result.place_name}</p>
                <p style={{
                  margin: 0,
                  fontSize: '12px',
                  color: '#6b7280'
                }}>
                  {(result.properties && result.properties.address) ||
                    (result.properties && result.properties.category) ||
                    (result.place_type && result.place_type.join(', '))}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {searchResults && searchResults.length === 0 && (
        <div style={{
          position: 'absolute',
          top: '60px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'white',
          padding: '8px 15px',
          borderRadius: '4px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
          zIndex: 10
        }}>
          No results found for these coordinates.
        </div>
      )}

      {/* Selected airport feature info panel */}
      {selectedFeature && (
        <div
          style={{
            position: 'absolute',
            right: '10px',
            top: '10px',
            width: '230px',
            padding: '10px',
            color: '#1a2224',
            fontSize: '12px',
            lineHeight: '20px',
            fontFamily: 'sans-serif'
          }}
        >
          <div
            style={{
              background: '#fff',
              padding: '10px',
              borderRadius: '3px',
              boxShadow: '0 0 10px rgba(0,0,0,0.1)'
            }}
          >
            <h3 style={{ margin: '0 0 5px 0' }}>{selectedFeature.properties.name}</h3>
            <hr style={{ border: '1px solid #eee', margin: '5px 0' }} />
            <ul style={{ padding: '0 0 0 15px', margin: '5px 0' }}>
              {selectedFeature.properties &&
                Object.entries(selectedFeature.properties)
                  .filter(([key]) => key !== 'name') // Skip name as it's already shown
                  .map(([key, value], idx) => (
                    <li key={idx}>
                      <b>{key}</b>: {value.toString()}
                    </li>
                  ))
              }
            </ul>
            <button
              onClick={() => {
                if (selectedFeatureRef.current) {
                  mapRef.current.setFeatureState(selectedFeatureRef.current, {
                    selected: false
                  });
                }
                setSelectedFeature(null);
              }}
              style={{
                background: '#4264fb',
                color: 'white',
                border: 'none',
                padding: '5px 10px',
                borderRadius: '3px',
                cursor: 'pointer',
                marginTop: '5px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Upload Assets Modal */}
      <Modal
        title={`Upload Assets for Marker ${currentMarkerIndex + 1}`}
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button key="cancel" onClick={handleModalClose}>
            Cancel
          </Button>,
          <Button
            key="upload"
            type="primary"
            onClick={handleUpload}
            disabled={!isUploadEnabled()}
          >
            Upload
          </Button>
        ]}
      >
        {/* Description Text Area */}
        <Row style={sectionStyle}>
          <Col span={24}>
            <h3 style={headingStyle}>
              {t('Marker Description')}
            </h3>
            <p style={descriptionStyle}>
              {t('Enter details about this location (max 3 lines)')}
            </p>
            <TextArea
              rows={3}
              placeholder="Enter description here..."
              value={markerDescription}
              onChange={handleDescriptionChange}
              style={textAreaStyle}
              maxLength={300}
            />
          </Col>
        </Row>

        <Row className="mt-4" style={sectionStyle}>
            <Col span={24}>
              <h3 className="upload-modal__text" style={headingStyle}>
                {t('Interactive Mode')}
              </h3>
              <p style={descriptionStyle}>
                {t('(Enable or disable 3D interaction)')}
              </p>
              <Switch checked={switchInteractive} onChange={onSwitchInteractionMode} />
            </Col>
          </Row>

        {/* AR Content Section - shown for both modes based on interactive toggle */}
        <Row className="mt-4" style={sectionStyle}>
          <Col span={24}>
            <h3 className="upload-modaltext" style={headingStyle}>
              {switchInteractive ? t('Interactive AR Content') : t('Non Interactive AR Content')}
            </h3>
            <p style={descriptionStyle}>
              {switchInteractive ? t('v2:_3d_files_mp4_ar') : t('v2:_3d_files_mp4')}
            </p>
            <div className="upload-modalinner" style={modalInnerStyle}>
              <div className="upload-modal__dropzone">
                <Dropzone
                  accept={[
                    'application/zip',
                    'application/x-zip-compressed',
                    'video/*'
                  ]}
                  maxFiles={1}
                  maxSize={60000000}
                  onDrop={onDropppedZip}
                >
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <div
                      className={className('upload-dropzone', {
                        'upload-dropzone_active': isDragActive
                      })}
                    >
                      <Spin size="large" spinning={zipLoading}>
                        <div className="upload-dropzonetext-wrapper">
                          <img src={uploadMedia} alt="" />
                          <p className="upload-dropzonetext" style={dropzoneTextStyle}>
                            <span>
                                  Upload 3D Zip
                            </span>{' '}
                            <br />
                            {t('v2:drag_n_drop')}
                          </p>
                          <br />
                          {zipSizeCheck && (
                            <em style={{ color: 'red' }}>
                              {t('v2:zip_size_should_not_exceed')}
                            </em>
                          )}
                          {!zipLoading && mediaDat.hasOwnProperty('ar_files') && (
                            <div>
                              <Progress type="circle" percent={100} width={30} />
                              <p className="upload-dropzone__text" style={dropzoneTextStyle}>
                                {isZip ? t('v2:content_attached') : t('v2:video_attached')}
                              </p>
                            </div>
                          )}
                        </div>
                      </Spin>
                    </div>
                  )}
                </Dropzone>
              </div>
            </div>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default Mapbox;